// Projects API client (Projects MVP Phase 4).
// Mirrors backend/app/schemas/projects.py + routers/projects.py.
// Auth + 401-refresh live in request() — same as the rest of the client.
import { request, ApiError, getToken } from "../api";
import type { Chat } from "../api";

// ---------- Types ----------

export interface Project {
  id: string;
  owner_id: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  default_model: string | null;
  is_pinned: boolean;
  is_archived: boolean;
  show_project_chats_in_global_chat: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectCreatePayload {
  name: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  default_model?: string | null;
  instructions?: string | null;
  show_project_chats_in_global_chat?: boolean;
}

export type ProjectUpdatePayload = Partial<ProjectCreatePayload>;

// Predefined palette only (design system: no free-form colors, no violet).
export const PROJECT_COLORS: { id: string; hex: string }[] = [
  { id: "cyan", hex: "#22d3ee" },
  { id: "blue", hex: "#38bdf8" },
  { id: "teal", hex: "#2dd4bf" },
  { id: "emerald", hex: "#34d399" },
  { id: "amber", hex: "#fbbf24" },
  { id: "orange", hex: "#fb923c" },
  { id: "rose", hex: "#fb7185" },
  { id: "slate", hex: "#94a3b8" },
];

export function projectColorHex(color: string | null | undefined): string {
  const found = PROJECT_COLORS.find((c) => c.id === color);
  return found ? found.hex : "#22d3ee";
}

export const PROJECT_ICONS: string[] = [
  "◆", "●", "■", "▲", "★", "⬢", "◎", "⬟", "✦", "◈",
];

// ---------- Projects CRUD (Phase 2 backend) ----------

export interface ListProjectsParams {
  search?: string;
  includeArchived?: boolean;
  limit?: number;
}

export async function fetchProjects(
  params: ListProjectsParams = {},
): Promise<Project[]> {
  const sp = new URLSearchParams();
  if (params.search) sp.set("search", params.search);
  if (params.includeArchived) sp.set("include_archived", "true");
  sp.set("limit", String(params.limit ?? 200));
  const q = sp.toString();
  return request<Project[]>(`/api/projects${q ? `?${q}` : ""}`);
}

export async function fetchProject(projectId: string): Promise<Project> {
  return request<Project>(`/api/projects/${projectId}`);
}

export async function createProject(
  data: ProjectCreatePayload,
): Promise<Project> {
  return request<Project>("/api/projects", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateProject(
  projectId: string,
  data: ProjectUpdatePayload,
): Promise<Project> {
  return request<Project>(`/api/projects/${projectId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteProject(projectId: string): Promise<void> {
  await request(`/api/projects/${projectId}`, { method: "DELETE" });
}

export async function archiveProject(projectId: string): Promise<Project> {
  return request<Project>(`/api/projects/${projectId}/archive`, {
    method: "POST",
  });
}

export async function restoreProject(projectId: string): Promise<Project> {
  return request<Project>(`/api/projects/${projectId}/restore`, {
    method: "POST",
  });
}

export async function pinProject(projectId: string): Promise<Project> {
  return request<Project>(`/api/projects/${projectId}/pin`, {
    method: "POST",
  });
}

export async function unpinProject(projectId: string): Promise<Project> {
  return request<Project>(`/api/projects/${projectId}/unpin`, {
    method: "POST",
  });
}

// ---------- Project chats (Phase 3 backend) ----------

export async function fetchProjectChats(
  projectId: string,
  limit = 50,
  includeArchived = false,
): Promise<Chat[]> {
  const q = `limit=${limit}&include_archived=${includeArchived ? "true" : "false"}`;
  return request<Chat[]>(`/api/projects/${projectId}/chats?${q}`);
}

export async function createProjectChat(
  projectId: string,
  title?: string,
  model?: string,
): Promise<Chat> {
  return request<Chat>(`/api/projects/${projectId}/chats`, {
    method: "POST",
    body: JSON.stringify({ title: title || "Новый чат", selected_model: model }),
  });
}

// ---------- Project files / file manager (Phase D/E backend) ----------
// Storage-only, reuse user_files. Folders are a VIRTUAL hierarchy in the DB.
// Endpoints live under /api/projects/{id}/files and /api/projects/{id}/folders.

export interface ProjectFileItem {
  id: string;
  name: string;
  mime?: string | null;
  size: number;
  kind: string;
  created_at: string;
  updated_at?: string | null;
  folder_id?: string | null;
}

export interface ProjectFolderItem {
  id: string;
  project_id: string;
  parent_folder_id: string | null;
  name: string;
  created_at: string;
  updated_at: string;
}

/** Grouped listing of ONE folder level (folders + files). */
export interface ProjectFilesPage {
  folders: ProjectFolderItem[];
  files: ProjectFileItem[];
  quota_used: number;
  quota_limit: number;
}

/** List one level. folderId=null/undefined = project root. */
export async function fetchProjectFiles(
  projectId: string,
  folderId?: string | null,
): Promise<ProjectFilesPage> {
  const q = folderId ? `?folder_id=${folderId}` : "";
  return request<ProjectFilesPage>(`/api/projects/${projectId}/files${q}`);
}

/** Direct children folders of a level (parentFolderId=null = root). */
export async function fetchProjectFolders(
  projectId: string,
  parentFolderId?: string | null,
): Promise<ProjectFolderItem[]> {
  const q = parentFolderId ? `?parent_id=${parentFolderId}` : "";
  return request<ProjectFolderItem[]>(`/api/projects/${projectId}/folders${q}`);
}

export async function createProjectFolder(
  projectId: string,
  name: string,
  parentFolderId?: string | null,
): Promise<ProjectFolderItem> {
  return request<ProjectFolderItem>(`/api/projects/${projectId}/folders`, {
    method: "POST",
    body: JSON.stringify(parentFolderId ? { name, parent_id: parentFolderId } : { name }),
  });
}

export async function renameProjectFolder(
  projectId: string,
  folderId: string,
  name: string,
): Promise<ProjectFolderItem> {
  return request<ProjectFolderItem>(
    `/api/projects/${projectId}/folders/${folderId}`,
    { method: "PATCH", body: JSON.stringify({ name }) },
  );
}

export async function deleteProjectFolder(
  projectId: string,
  folderId: string,
): Promise<void> {
  await request(`/api/projects/${projectId}/folders/${folderId}`, { method: "DELETE" });
}

/**
 * Move a folder to another level (Phase I.1, D1).
 * parentFolderId=null/undefined = project root. The backend enforces
 * project ownership, self-move (400) and cycle (400) protection.
 */
export async function moveProjectFolder(
  projectId: string,
  folderId: string,
  parentFolderId: string | null,
): Promise<ProjectFolderItem> {
  return request<ProjectFolderItem>(
    `/api/projects/${projectId}/folders/${folderId}`,
    { method: "PATCH", body: JSON.stringify({ parent_folder_id: parentFolderId }) },
  );
}

/**
 * Upload a file into a folder (folderId=null/undefined = project root).
 * The folder target is enforced server-side (foreign folder -> 404).
 */
export async function uploadProjectFile(
  projectId: string,
  file: File,
  folderId?: string | null,
): Promise<ProjectFileItem & { quota_used: number; quota_limit: number }> {
  const token = getToken();
  const form = new FormData();
  form.append("file", file);
  if (folderId) form.append("folder_id", folderId);
  const res = await fetch(`/api/projects/${projectId}/files`, {
    method: "POST",
    headers: token ? { Authorization: "Bearer " + token } : {},
    body: form,
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      if (data.detail)
        detail = typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, detail);
  }
  return res.json() as Promise<ProjectFileItem & { quota_used: number; quota_limit: number }>;
}

export async function deleteProjectFile(
  projectId: string,
  fileId: string,
): Promise<void> {
  await request(`/api/projects/${projectId}/files/${fileId}`, { method: "DELETE" });
}

/** Rename and/or move. Pass only the fields you want to change. */
export async function updateProjectFile(
  projectId: string,
  fileId: string,
  patch: { name?: string; folder_id?: string | null },
): Promise<ProjectFileItem> {
  const body: Record<string, unknown> = {};
  if (patch.name !== undefined) body.name = patch.name;
  if ("folder_id" in patch) body.folder_id = patch.folder_id;
  return request<ProjectFileItem>(`/api/projects/${projectId}/files/${fileId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/**
 * Download a project file with the auth header (the endpoint requires it —
 * a plain `<a href>` would 401). Returns the bytes; the caller triggers the
 * browser download via an object URL.
 */
export async function downloadProjectFile(
  projectId: string,
  fileId: string,
): Promise<Blob> {
  const token = getToken();
  const res = await fetch(`/api/projects/${projectId}/files/${fileId}/download`, {
    headers: token ? { Authorization: "Bearer " + token } : {},
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      if (data.detail)
        detail = typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, detail);
  }
  return res.blob();
}

/**
 * Fetch a project file's bytes for in-app PREVIEW (md/pdf/docx).
 * Uses the authenticated /preview endpoint (same auth as download; the file
 * is never made public). The caller MUST revoke the returned object URL.
 */
export async function previewProjectFile(
  projectId: string,
  fileId: string,
): Promise<Blob> {
  const token = getToken();
  const res = await fetch(`/api/projects/${projectId}/files/${fileId}/preview`, {
    headers: token ? { Authorization: "Bearer " + token } : {},
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      if (data.detail)
        detail = typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, detail);
  }
  return res.blob();
}

// ---------- Project Memory (Phase 7) ----------

export type MemoryType =
  | "fact" | "decision" | "preference" | "constraint" | "architecture" | "warning";

export const MEMORY_TYPES: MemoryType[] = [
  "fact", "decision", "preference", "constraint", "architecture", "warning",
];

export interface Memory {
  id: string;
  type: MemoryType;
  key: string;
  value: string;
  source: string | null;
  confidence: number | null;
  created_at: string;
  updated_at: string;
}

export interface MemoryCreatePayload {
  type: MemoryType;
  key: string;
  value: string;
  source?: string | null;
  confidence?: number | null;
}

export type MemoryUpdatePayload = Partial<MemoryCreatePayload>;

export async function fetchProjectMemory(projectId: string): Promise<Memory[]> {
  return request<Memory[]>(`/api/projects/${projectId}/memory`);
}

export async function createProjectMemory(
  projectId: string,
  payload: MemoryCreatePayload,
): Promise<Memory> {
  return request<Memory>(`/api/projects/${projectId}/memory`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateProjectMemory(
  projectId: string,
  memoryId: string,
  payload: MemoryUpdatePayload,
): Promise<Memory> {
  return request<Memory>(`/api/projects/${projectId}/memory/${memoryId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteProjectMemory(
  projectId: string,
  memoryId: string,
): Promise<void> {
  await request(`/api/projects/${projectId}/memory/${memoryId}`, { method: "DELETE" });
}

// ---------- Project Knowledge Base (RAG, Phase H) ----------
// A separate retrieval layer over the project's files (NOT memory, NOT the
// file manager). Manual ingestion of TXT/Markdown/PDF; project-scoped
// PostgreSQL full-text search. No mock data.

export type KnowledgeStatus = "pending" | "ready" | "failed" | "archived";

export interface KnowledgeChunk {
  seq: number;
  content: string;
  char_count: number;
}

export interface KnowledgeDoc {
  id: string;
  user_file_id: string;
  file_name: string;
  file_mime: string | null;
  status: KnowledgeStatus;
  error: string | null;
  chunk_count: number;
  source_hash: string | null;
  indexed_version: number;
  indexed_at: string | null;
  created_at: string;
  updated_at: string;
  chunks?: KnowledgeChunk[] | null;
}

export interface KnowledgeSearchHit {
  content: string;
  knowledge_id: string;
  seq: number;
  file_name: string | null;
  source_hash: string | null;
}

export interface KnowledgeSearchResult {
  query: string;
  k: number;
  results: KnowledgeSearchHit[];
}

export async function fetchProjectKnowledge(projectId: string): Promise<KnowledgeDoc[]> {
  return request<KnowledgeDoc[]>(`/api/projects/${projectId}/knowledge`);
}

export async function getProjectKnowledge(
  projectId: string,
  knowledgeId: string,
): Promise<KnowledgeDoc> {
  return request<KnowledgeDoc>(`/api/projects/${projectId}/knowledge/${knowledgeId}`);
}

export async function ingestProjectKnowledge(
  projectId: string,
  userFileId: string,
): Promise<KnowledgeDoc> {
  return request<KnowledgeDoc>(`/api/projects/${projectId}/knowledge`, {
    method: "POST",
    body: JSON.stringify({ user_file_id: userFileId }),
  });
}

export async function reindexProjectKnowledge(
  projectId: string,
  knowledgeId: string,
): Promise<KnowledgeDoc> {
  return request<KnowledgeDoc>(`/api/projects/${projectId}/knowledge/${knowledgeId}/reindex`, {
    method: "POST",
  });
}

export async function deleteProjectKnowledge(
  projectId: string,
  knowledgeId: string,
): Promise<void> {
  await request(`/api/projects/${projectId}/knowledge/${knowledgeId}`, { method: "DELETE" });
}

export async function searchProjectKnowledge(
  projectId: string,
  query: string,
  k = 8,
): Promise<KnowledgeSearchResult> {
  const q = `q=${encodeURIComponent(query)}&k=${k}`;
  return request<KnowledgeSearchResult>(`/api/projects/${projectId}/knowledge/search?${q}`);
}

// ---------- Error helpers ----------

/** 404 = foreign OR unknown project — identical detail (anti-enumeration).
 *  The UI must NOT reveal that a project exists: one generic message. */
export function projectNotFoundMessage(): string {
  return "Проект не найден";
}

export function isProjectNotFound(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404;
}

// ---------- Project Tasks (Phase I) ----------

export type TaskStatus = "todo" | "in_progress" | "done" | "archived";
export type TaskPriority = "low" | "normal" | "high";

export const TASK_STATUSES: TaskStatus[] = [
  "todo", "in_progress", "done", "archived",
];
export const TASK_PRIORITIES: TaskPriority[] = ["low", "normal", "high"];

export interface Task {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  created_by: string;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface TaskCreatePayload {
  title: string;
  description?: string | null;
  status?: TaskStatus | null;
  priority?: TaskPriority | null;
  assigned_to?: string | null;
}

export type TaskUpdatePayload = Partial<TaskCreatePayload>;

export async function fetchProjectTasks(
  projectId: string,
  status?: TaskStatus,
): Promise<Task[]> {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  return request<Task[]>(`/api/projects/${projectId}/tasks${q}`);
}

export async function getProjectTask(
  projectId: string,
  taskId: string,
): Promise<Task> {
  return request<Task>(`/api/projects/${projectId}/tasks/${taskId}`);
}

export async function createProjectTask(
  projectId: string,
  payload: TaskCreatePayload,
): Promise<Task> {
  return request<Task>(`/api/projects/${projectId}/tasks`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateProjectTask(
  projectId: string,
  taskId: string,
  payload: TaskUpdatePayload,
): Promise<Task> {
  return request<Task>(`/api/projects/${projectId}/tasks/${taskId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteProjectTask(
  projectId: string,
  taskId: string,
): Promise<void> {
  await request(`/api/projects/${projectId}/tasks/${taskId}`, { method: "DELETE" });
}
